<template>
    <TransitionRoot as="template" :show="show">
        <Dialog as="div" class="fixed inset-0 overflow-hidden" @close="close()">
            <div class="absolute inset-0 overflow-hidden">
                <DialogOverlay class="absolute inset-0 bg-gray-500 opacity-75"/>

                <div class="fixed inset-y-0 pl-16 max-w-full right-0 flex">
                    <TransitionChild as="template" enter="transform transition ease-in-out duration-500 sm:duration-700"
                                     enter-from="translate-x-full" enter-to="translate-x-0"
                                     leave="transform transition ease-in-out duration-500 sm:duration-700"
                                     leave-from="translate-x-0" leave-to="translate-x-full">
                        <div class="w-screen max-w-2xl">
                            <form class="h-full divide-y divide-gray-200 flex flex-col bg-white shadow-xl"
                                  @submit.prevent="submit">
                                <div class="flex-1 h-0 overflow-y-auto">
                                    <div class="py-6 px-4  bg-gradient-to-b from-pitch-purple to-dark-pitch-purple sm:px-6">
                                        <div class="flex items-center justify-between">
                                            <DialogTitle class="text-lg text-white" v-if="form.id">
                                                Update Expense
                                            </DialogTitle>
                                            <DialogTitle class="text-lg text-white" v-else>
                                                Create Expense
                                            </DialogTitle>
                                            <div class="ml-3 h-7 flex items-center">
                                                <button type="button"
                                                        class="bg-dark-pitch-purple rounded-md text-indigo-200 hover:text-white focus:outline-none focus:ring-2 focus:ring-white"
                                                        @click="close()">
                                                    <span class="sr-only">Close panel</span>
                                                    <XIcon class="h-6 w-6" aria-hidden="true"/>
                                                </button>
                                            </div>
                                        </div>
                                        <div class="mt-1">
                                            <p class="text-sm text-indigo-300">
                                                Get started by filling in the information below to create a new expense.
                                            </p>
                                        </div>
                                    </div>
                                    <SlideOutErrors :show="showErrors === true"/>
                                    <div class="flex-1 flex flex-col justify-between">
                                        <div class="px-4 divide-y divide-gray-200 sm:px-6">
                                            <div class="mt-6 grid grid-cols-2 gap-4">
                                                <div class="col-span-2">
                                                    <h3 class="text-lg leading-6 font-medium text-gray-900">
                                                        Expense Information
                                                    </h3>
                                                </div>
                                                <div>
                                                    <label for="employee_name" class="block text-sm text-gray-900 required">
                                                        Employee Name
                                                    </label>
                                                    <div class="mt-1">
                                                        <select id="employee_name" v-model="form.user_id" :disabled="form.user_id !== 1 && form.user_id !== 2 && form.user_id !== 4"
                                                                :class="{'border-red-500': form.errors.user_id}"
                                                                class="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm">
                                                            <option value selected v-html="'Select an option'"></option>
                                                            <option v-for="(user,index) in users" v-html="user.name" :value="user.id"></option>
                                                        </select>
                                                    </div>
                                                </div>
                                                <div>
                                                    <label for="paid_at_date"
                                                           class="block text-sm text-gray-900 required">
                                                        Transaction Date <span v-if="formattedTransactionDate">({{ formattedTransactionDate }})</span>
                                                    </label>
                                                    <div class="mt-1">
                                                        <input type="date" v-model="form.paid_at" id="paid_at_date"
                                                               class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                               :class="{'border-red-500': form.errors.paid_at}"
                                                        />
                                                    </div>
                                                </div>

                                                <div class="col-span-2">
                                                    <label for="fixture"
                                                           class="block text-sm text-gray-900 required">
                                                        Fixture
                                                    </label>
                                                    <div class="mt-1">
                                                        <v-select class="mt-1 block w-100 bg-white rounded-md shadow-sm focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"
                                                            :options="fixtures" label="expense_fixture_option" :reduce="fixtures => fixtures.id" v-model="form.fixture_id" placeholder="Fixtures" />
                                                    </div>
                                                </div>

                                                <div class="col-span-2">
                                                    <label for="payment-card"
                                                           class="block text-sm text-gray-900 required">
                                                        Payment Card
                                                    </label>
                                                    <div class="mt-1">
                                                        <select id="payment-card" v-model="form.card_id"
                                                                :class="{'border-red-500': form.errors.card_id}"
                                                                class="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm">
                                                            <option value selected
                                                                    v-html="'Select a payment card'"></option>
                                                            <option v-for="(card,index) in cards"
                                                                    v-html="card.name + ', ending '+ card.card_number.slice(12) + ', expires ' + card.expiry_month + '/' + card.expiry_year.slice(2)"
                                                                    :value="card.id"></option>
                                                        </select>
                                                    </div>
                                                </div>

                                                <div class="col-span-2">
                                                    <label for="expense-supplier"
                                                            class="block text-sm text-gray-900 required">
                                                        Supplier
                                                    </label>
                                                    <div class="mt-1">
                                                        <input type="text" id="expense-supplier" v-model="form.supplier"
                                                               class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                               :class="{'border-red-500': form.errors.supplier}"
                                                        />
                                                    </div>
                                                </div>

                                                <div class="col-span-2">
                                                    <h3 class="text-lg leading-6 font-medium text-gray-900">Deposit
                                                        Information</h3>
                                                </div>
                                                <div>
                                                    <label for="expense-amount"
                                                           class="block text-sm text-gray-900 required">
                                                        Expense Amount
                                                    </label>
                                                    <div class="mt-1">
                                                        <input type="text" id="expense-amount" v-model="form.amount"
                                                               class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                               :class="{'border-red-500': form.errors.amount}"
                                                        />
                                                    </div>
                                                </div>

                                                <div>
                                                    <label for="gbp-amount" class="block text-sm text-gray-900 required">
                                                        {{ fixture?.primary_trading_details?.currency?.code || 'GBP' }} Amount
                                                    </label>
                                                    <div class="mt-1">
                                                        <input type="text" id="gbp-amount" v-model="form.gbp_amount"
                                                               class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                               :class="{'border-red-500': form.errors.gbp_amount}"
                                                        />
                                                    </div>
                                                </div>
                                                <div>
                                                    <label for="vat_type"
                                                           class="block text-sm text-gray-900 required">
                                                        {{ fixture?.primary_trading_details?.tax_wording || 'VAT' }} Amount Types
                                                    </label>
                                                    <div class="mt-1">
                                                        <select id="vat_type" v-model="form.vat_type"
                                                                :class="{'border-red-500': form.errors.vat_type}"
                                                                class="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm">
                                                            <option value selected
                                                                    v-html="'Select a VAT Type'"></option>
                                                            <option value="VAT Receipt">VAT Receipt</option>
                                                            <option value="No VAT Receipt">No VAT Receipt</option>
                                                            <option value="Missing/Lost">Missing/Lost</option>
                                                        </select>
                                                    </div>
                                                </div>
                                                <div>
                                                    <label for="vat_amount"
                                                           class="block text-sm text-gray-900 required">
                                                        {{ fixture?.primary_trading_details?.tax_wording || 'VAT' }} Amount
                                                    </label>
                                                    <div class="mt-1">
                                                        <input type="text" id="vat_amount" v-model="form.vat_amount"
                                                               class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                               :class="{'border-red-500': form.errors.vat_amount}"
                                                        />
                                                    </div>
                                                </div>
                                                <div>
                                                    <label for="expense_type_id"
                                                           class="block text-sm text-gray-900 required">
                                                        Expense Type
                                                    </label>
                                                    <div class="mt-1">
                                                        <select id="expense_type_id" v-model="form.expense_type_id"
                                                                :class="{'border-red-500': form.errors.expense_type_id}"
                                                                class="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm">
                                                            <option value selected
                                                                    v-html="'Select an expense type'"></option>
                                                            <option v-for="(expense_type,index) in expense_types"
                                                                    v-html="expense_type.name"
                                                                    :value="expense_type.id"></option>
                                                        </select>
                                                    </div>
                                                </div>
                                                <div>
                                                    <label for="transaction_type_id"
                                                           class="block text-sm text-gray-900 required">
                                                        Transaction Type
                                                    </label>
                                                    <div class="mt-1">
                                                        <select id="transaction_type_id" v-model="form.transaction_type_id" :disabled="form.fixture_id !== 0"
                                                                :class="{'border-red-500': form.errors.transaction_type_id}"
                                                                class="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm">
                                                            <option value selected
                                                                    v-html="'Select a transaction type'"></option>
                                                            <option
                                                                :disabled="transaction_type.id === 25 && form.fixture_id === 0"
                                                                v-for="(transaction_type,index) in transaction_types"
                                                                v-html="transaction_type.name + ' (' + transaction_type.title + ')'"
                                                                :value="transaction_type.id"></option>
                                                        </select>
                                                    </div>
                                                </div>
                                                <div>
                                                    <label for="currency_id"
                                                           class="block text-sm text-gray-900 required">
                                                        Currency
                                                    </label>
                                                    <div class="mt-1">
                                                        <select id="currency_id" v-model="form.currency_id"
                                                                :class="{'border-red-500': form.errors.currency_id}"
                                                                class="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm">
                                                            <option value selected
                                                                    v-html="'Select a currency'"></option>
                                                            <option
                                                                v-for="(currency,index) in currencies"
                                                                v-html="currency.symbol + ' - ' + currency.name"
                                                                :value="currency.id"></option>
                                                        </select>
                                                    </div>
                                                </div>
                                                <div>
                                                    <label for="status"
                                                           class="block text-sm text-gray-900 required">
                                                        Status
                                                    </label>
                                                    <div class="mt-1">
                                                        <select id="status" v-model="form.status"
                                                                :class="{'border-red-500': form.errors.status}"
                                                                class="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm">
                                                            <option value selected
                                                                    v-html="'Select an expense status'"></option>
                                                            <option v-for="(status,index) in statuses" :value="status"
                                                                    v-html="status.charAt(0).toUpperCase() + status.substring(1)"></option>
                                                        </select>
                                                    </div>
                                                </div>
                                                <div class="col-span-2">
                                                    <label for="description"
                                                           class="block text-sm text-gray-900">
                                                        Description
                                                    </label>
                                                    <div class="mt-1">
                                                        <quill-editor content-type="html"
                                                                      v-model:content="form.description"
                                                                      class="min-h-[150px] mt-1 block w-full bg-white border border-gray-300 shadow-sm py-2 px-3"
                                                                      theme="snow"></quill-editor>
                                                    </div>
                                                </div>
                                                <div class="col-span-2">
                                                    <label
                                                           class="block text-sm text-gray-900">
                                                        Attachments
                                                    </label>
                                                    <div class="mt-1">
                                                        <dd class="my-2 text-sm text-gray-900" v-if="form.receipts.length > 0">
                                                            <ul role="list" class="border border-gray-200 rounded-md divide-y divide-gray-200">
                                                                <li class="pl-3 pr-4 py-3 flex items-center justify-between text-sm" v-for="(receipt,index) in this.form.receipts">
                                                                    <div class="w-0 flex-1 flex items-center">
                                                                        <PaperClipIcon class="flex-shrink-0 h-5 w-5 text-gray-400" aria-hidden="true" />
                                                                        <span class="ml-2 flex-1 w-0 truncate"> {{ receipt }} </span>
                                                                    </div>
                                                                    <div class="ml-4 flex-shrink-0">
                                                                        <a :href="route('frontend.expenses.expense.download', [this.form.id, receipt])" class="font-medium text-pitch-purple hover:text-light-pitch-purple"> <DownloadIcon class="w-4 h-4" /> </a>
                                                                    </div>
                                                                </li>
                                                            </ul>
                                                        </dd>
                                                        <label class="w-full mb-4 flex flex-col items-center px-4 py-6 bg-white text-blue rounded-lg tracking-wide uppercase border border-blue cursor-pointer">
                                                            <svg class="w-8 h-8" fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                                                                <path d="M16.88 9.1A4 4 0 0 1 16 17H5a5 5 0 0 1-1-9.9V7a3 3 0 0 1 4.52-2.59A4.98 4.98 0 0 1 17 8c0 .38-.04.74-.12 1.1zM11 11h3l-4-4-4 4h3v3h2v-3z" />
                                                            </svg>
                                                            <span v-if="form.file_uploads.length  === 0" class="mt-2 text-base leading-normal">Select a file</span>
                                                            <span v-else>{{ form.file_uploads.length }} file<span v-if="form.file_uploads.length > 1">s</span> selected for upload</span>
                                                            <input type='file' class="hidden" multiple @change="prepareFileUpload($event)" />
                                                        </label>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                </div>
                                <div class="flex-shrink-0 px-4 py-4 flex justify-end">
                                    <button type="button"
                                            class="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                            @click="close()">
                                        Close
                                    </button>
                                    <button :disabled="form.processing || !form.isDirty" type="submit"
                                            class=" ml-4 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm rounded-md text-white bg-pitch-purple hover:bg-dark-pitch-purple focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-200">
                                        <span v-if="updating === true">Update</span>
                                        <span v-else>Save</span>
                                    </button>
                                </div>
                            </form>
                        </div>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>
<script>
import {ref, reactive} from 'vue'
import {Dialog, DialogOverlay, DialogTitle, TransitionChild, TransitionRoot} from '@headlessui/vue'
import {XIcon} from '@heroicons/vue/outline'
import {LinkIcon, PlusSmIcon, QuestionMarkCircleIcon, ExclamationCircleIcon, UploadIcon, PaperClipIcon, DownloadIcon} from '@heroicons/vue/solid'
import {useForm, usePage} from '@inertiajs/vue3'
import { router } from '@inertiajs/vue3'
import SlideOutErrors from "@/Components/SlideOutErrors";
import {mapState} from "vuex";
import {emitter} from "@/eventHub";
import moment from "moment/moment";


export default {
    components: {
        SlideOutErrors,
        Dialog,
        DialogOverlay,
        DialogTitle,
        TransitionChild,
        TransitionRoot,
        LinkIcon,
        PlusSmIcon,
        QuestionMarkCircleIcon,
        ExclamationCircleIcon,
        XIcon,
        UploadIcon,
        PaperClipIcon,
        DownloadIcon
    },
    computed: {
        ...mapState({
            user: state => state.expenses.user ?? null,
            users: state => state.expenses.users ?? null,
            fixtures: state => state.expenses.fixtures ?? null,
            expense_types: state => state.expenses.expense_types ?? null,
            transaction_types: state => state.expenses.transaction_types ?? null,
            vat_rates: state => state.expenses.vat_rates ?? null,
            vat_types: state => state.expenses.vat_types ?? null,
            cards: state => state.expenses.cards ?? null,
            currencies: state => state.expenses.currencies ?? null,
            statuses: state => state.expenses.statuses ?? null
        }),
        formattedTransactionDate() {
            if (this.form.paid_at) {
                return moment(this.form.paid_at).format('DD MMM YYYY');
            }
            return '';
        },
    },
    setup(prop, context) {

        const user_role = usePage().props.auth.user_role

        const show = ref(false)

        const showErrors = ref(false)

        const updating = false;

        const fixture = usePage().props.fixture;

        const fixture_dropdown_disabled = false;

        let submitting = false;

        const form = useForm({
            id: null,
            user_id: "",
            paid_at: "",
            amount: null,
            gbp_amount: null,
            vat_amount: null,
            expense_type_id: "",
            transaction_type_id: "",
            status : "",
            currency_id: "",
            description: "",
            receipts: [],
            card_id: "",
            fixture_id: "",
            file_uploads: [],
            supplier: "",
            vat_type: "",
        })

        function submit() {
            if (submitting || form.processing) {
                return;
            }

            submitting = true;

            const options = {
                onSuccess: () => {
                    show.value = false;
                    showErrors.value = false;
                    form.reset();
                },
                onError: () => {
                    showErrors.value = true;
                },
                onFinish: () => {
                    submitting = false;
                },
            };

            if (form.id) {
                form.post(route('frontend.expenses.expense.update', form.id), options);
            } else {
                form.post(route('frontend.expenses.store'), options);
            }
        }

        function close() {
            this.form.reset();
            this.form.clearErrors();
            this.updating = false;
            let fixture_dropdown = document.getElementById('fixture_dropdown');
            this.fixture_dropdown_disabled = false
            this.show = false;
        }

        function open() {
            console.log({user_role});
            this.$store.dispatch('expenses/getData', {
                fixture_id: typeof this.form.fixture_id !== "undefined" ? this.form.fixture_id : null
            });
            if (this.updating == false) {
                this.form.user_id = usePage().props.auth.user.id
            }
            this.form.clearErrors();
            this.show = true;
        }

        const emitter = emitter;

        return {
            show,
            form,
            submit,
            updating,
            close,
            open,
            showErrors,
            emitter,
            fixture_dropdown_disabled,
            fixture
        }
    },
    mounted() {
        emitter.on('openExpenseSlideout', (fixture) => {
            if (typeof fixture !== 'undefined') {
                this.form.fixture_id = fixture.id
                // Disable the fixture dropdown.
                this.fixture_dropdown_disabled = true
            }
            this.open();
        });

        emitter.on('editExpenseSlideout', (expense) => {
            this.updating = true;
            this.form.id = expense.id;
            this.form.description = expense.description;
            this.form.expense_type_id = expense.expense_type_id;
            this.form.transaction_type_id = expense.transaction_type_id
            this.form.amount = expense.amount,
            this.form.gbp_amount = expense.gbp_amount,
            this.form.vat_amount = expense.vat_amount
            this.form.user_id = expense.user_id
            this.form.currency_id = expense.currency_id
            this.form.status = expense.status
            this.form.fixture_id = expense.fixture_id ?? ""
            this.form.card_id = expense.card_id
            this.form.paid_at = expense.paid_at
            this.form.receipts = expense.receipts ?? [],
            this.form.supplier = expense.supplier,
            this.form.currency_id = expense.currency_id,
            this.form.status = expense.status,
            this.form.vat_type = expense.vat_type,
            this.open();
        })

    },
    methods: {
        prepareFileUpload(event) {
            this.form.file_uploads = event.target.files
        }
    },
    watch: {
        'form.fixture_id': function(newVal) {
            if (newVal !== 0) {
                this.form.transaction_type_id = 25;
            } else {
                this.form.transaction_type_id = '';
            }
        },
    }
}
</script>
